import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import { BUTTON_TYPE, EMPTY_STATE_SIZE, MENU_OPTIONS } from '../../../../global/constants';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import { MilestoneItemContent, Second, SVGDiv, MilestoneEmpty, Data, ShowTask, ShowHide, Content } from './styles';
import MilestoneHeader from './milestoneHeader';
import Button from '../../../../component/button';
import { useMobileDevice } from '../../../../global/useMobile';
import { VIEW_ANALYTICS } from '../../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { useHistory } from 'react-router-dom';
import { updateMilestone } from '../../../../services/projectServices';
import { DropdownItem } from '../../../../component/dropdown';
import { captureException } from '../../../../services/logService';
import { menuEditItems, menuRestoreItems } from '../../../../global/row';
import { rootStore } from '../../../../mobx/rootStore';
import { Milestone } from '../../../../mobx/interfaces/project';
import { TaskModel } from '../../../../mobx/models/task';
import MyCustomContextMenuV2 from '../../../../component/customContextMenu';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../../../component/emptyState';
import { ROUTES } from '../../../../routes/urls';
import MilestoneTaskRow from './milestoneTaskRow';
import { ProjectModel } from '../../../../mobx/models/project';
import { UserModel } from '../../../../mobx/models/user';
import type { DraggableAttributes } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { useMaxVisibleLabels } from '../../../../hooks/useMaxVisibleLabels';

// Wrapper component to prevent unnecessary re-renders
interface MilestoneTaskRowWrapperProps {
  task: TaskModel;
  currentProject: ProjectModel;
  loadData: () => void;
  item: Milestone;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onClickTaskCheckBox: (task: TaskModel, value: boolean) => void;
  onOpenDeleteTask: (task: TaskModel) => void;
  onOpenTaskModal: () => void;
  currentUser: UserModel;
  onClickUsers: (taskId: string) => void;
  isMilestonePage: boolean;
  maxVisibleLabels: number;
}

const MilestoneTaskRowWrapper: React.FC<MilestoneTaskRowWrapperProps> = React.memo(
  ({
    task,
    currentProject,
    loadData,
    item,
    onChangeStatus,
    onClickTaskCheckBox,
    onOpenDeleteTask,
    onOpenTaskModal,
    currentUser,
    onClickUsers,
    isMilestonePage,
    maxVisibleLabels
  }) => {
    const history = useHistory();

    const handleView = useCallback(() => {
      if (!task) return;
      const eventProperties = {
        source: 'milestone task list'
      };
      trackAnalyticActivity(VIEW_ANALYTICS.MILESTONE_TASK, eventProperties);
      rootStore.taskStore.setCurrentTask(task?.id);
      history.push(ROUTES.TASK_DETAILS.replace(':id', task?.projectId || '').replace(':taskId', task?.id || ''));
    }, [task, history]);

    const handleClone = useCallback(() => {
      rootStore.taskStore.createTaskInput.updateWith(task);
      rootStore.taskStore.createTaskInput.setDueDate(null);
      rootStore.taskStore.createTaskInput.setId(undefined);
      // Set cloneTaskId if the task has subtasks
      if (task.subTasks && task.subTasks.length > 0) {
        rootStore.taskStore.createTaskInput.setCloneTaskId(task.id);
      }
      onOpenTaskModal();
    }, [task, onOpenTaskModal]);

    const handleEdit = useCallback(() => {
      rootStore.taskStore.createTaskInput.updateWith(task);
      onOpenTaskModal();
    }, [task, onOpenTaskModal]);

    const handleDelete = useCallback(() => {
      if (task) onOpenDeleteTask(task);
    }, [task, onOpenDeleteTask]);

    const handleUpdateStatus = useCallback(
      (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => {
        onChangeStatus(task, status, previousStatus);
      },
      [onChangeStatus]
    );

    const handleTaskCheckBox = useCallback(
      (task: TaskModel, value: boolean) => {
        onClickTaskCheckBox(task, value);
      },
      [onClickTaskCheckBox]
    );

    return (
      <MyCustomContextMenuV2
        key={`${item?.id}-${task?.id}`}
        taskData={task}
        loadData={loadData}
        currentProject={currentProject}
        onClickView={handleView}
        onClickClone={handleClone}
        onClickEdit={handleEdit}
        statusList={currentProject?.statusData || []}
        onClickDelete={handleDelete}
        onUpdateStatus={handleUpdateStatus}
        isMilestonePage={isMilestonePage}>
        <MilestoneTaskRow
          task={task}
          currentProject={currentProject}
          onClickMilestoneTask={handleView}
          onClickTaskCheckBox={handleTaskCheckBox}
          onClickUsers={onClickUsers}
          userDetails={currentUser}
          maxVisibleLabels={maxVisibleLabels}
          onChangeStatus={handleUpdateStatus}
        />
      </MyCustomContextMenuV2>
    );
  }
);

MilestoneTaskRowWrapper.displayName = 'MilestoneTaskRowWrapper';

type SortableListeners = ReturnType<typeof useSortable>['listeners'];

interface Props {
  item: Milestone;
  isMilestoneItem: boolean;
  loadData: () => void;
  milestoneIndex: number;
  newOldestDate: string | null;
  newestDueDate: string | null;
  closeArchiveModal: () => void;
  selectedId: string;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onClickTaskCheckBox: (task: TaskModel, value: boolean) => void;
  // Action callbacks to replace individual state setters
  onOpenDeleteModal: (milestoneId: string) => void;
  onOpenArchiveModal: (milestoneId: string) => void;
  onOpenEditModal: (milestone: Milestone) => void;
  onOpenCreateTask: (milestone: Milestone) => void;
  onOpenDeleteTask: (task: TaskModel) => void;
  onOpenTaskModal: () => void;
  onToggleCompletedTask: (value: boolean, itemId: string) => void;
  onSetButtonLoading: (value: boolean) => void;
  isSortableItem?: boolean;
  sortableAttributes?: DraggableAttributes;
  sortableListeners?: SortableListeners;
}
const MilestoneItem: React.FC<Props> = (props) => {
  const {
    item,
    isMilestoneItem,
    loadData,
    newestDueDate,
    newOldestDate,
    closeArchiveModal,
    selectedId,
    onChangeStatus,
    onClickTaskCheckBox,
    onOpenDeleteModal,
    onOpenArchiveModal,
    onOpenEditModal,
    onOpenCreateTask,
    onOpenDeleteTask,
    onOpenTaskModal,
    onToggleCompletedTask,
    onSetButtonLoading,
    isSortableItem,
    sortableAttributes,
    sortableListeners
  } = props;

  //States
  const [showCompletedTask, setShowCompletedTask] = useState(false);
  const maxVisibleLabels = useMaxVisibleLabels();

  // Create a fingerprint that changes when any task's observable properties change
  const taskStatusFingerprint = item?.tasks?.map((task) => `${task.id}-${task.status}`).join(',') || '';

  // Memoized task filtering - re-runs when task statuses actually change
  const uncompletedTaskList = useMemo(
    () => item?.tasks?.filter((task) => task?.status !== true) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskStatusFingerprint]
  );
  const completedTaskList = useMemo(
    () => item?.tasks?.filter((task) => task?.status === true) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskStatusFingerprint]
  );

  // Mobx store variables
  const { projectStore, currentUser } = rootStore;
  const { currentProject } = projectStore;

  // Other variables
  const mobile = useMobileDevice();

  //handle edit milestone
  const onClickEdit = useCallback(
    (item: Milestone) => {
      onOpenEditModal(item);
    },
    [onOpenEditModal]
  );

  //open archive milestone model
  const openArchiveMilestoneModel = useCallback(
    (itemId: string) => {
      onOpenArchiveModal(itemId);
    },
    [onOpenArchiveModal]
  );

  //handle retrieve milestone
  const onClickRetrieve = useCallback(
    async (itemId: string) => {
      try {
        onSetButtonLoading(true);
        const payload = {
          milestoneId: itemId,
          isArchived: false
        };
        const result = await updateMilestone(payload);
        if (result) {
          closeArchiveModal();
          loadData();
        }
      } catch (error) {
        captureException(error);
      } finally {
        onSetButtonLoading(false);
      }
    },
    [closeArchiveModal, loadData, onSetButtonLoading]
  );

  //handle milestone dropdown menu click`
  const handleMenuClick = useCallback(
    (key: number | string, item: Milestone) => {
      key = Number(key);
      if (key === MENU_OPTIONS.EDIT) {
        onClickEdit(item);
      } else if (key === MENU_OPTIONS.ARCHIVE) {
        openArchiveMilestoneModel(item?.id);
      }
    },
    [onClickEdit, openArchiveMilestoneModel]
  );

  //delete milestone model
  const openDeleteMilestoneModel = useCallback(
    (itemId: string) => {
      onOpenDeleteModal(itemId);
    },
    [onOpenDeleteModal]
  );

  //create milestone task model
  const openCreateTaskModel = useCallback(
    async (milestone: Milestone) => {
      onOpenCreateTask(milestone);
    },
    [onOpenCreateTask]
  );

  //handle archive dropdown menu option click
  const handleMenuOptionClick = useCallback(
    (key: number | string, item: Milestone) => {
      key = Number(key);
      if (key === MENU_OPTIONS.RESTORE) {
        onClickRetrieve(item?.id);
      } else if (key === MENU_OPTIONS.DELETE) {
        openDeleteMilestoneModel(item?.id);
      }
    },
    [onClickRetrieve, openDeleteMilestoneModel]
  );

  useEffect(() => {
    return () => {
      rootStore.taskStore.clearCurrentAssignBoxTaskId();
    };
  }, []);

  //toggle completed task
  const toggleCompletedTask = useCallback(
    (value: boolean, itemId: string) => {
      onToggleCompletedTask(value, itemId);
      setShowCompletedTask(value);
    },
    [onToggleCompletedTask]
  );

  //handle assign dropdown click
  const onClickUsers = useCallback(
    (taskId: string) => {
      if (mobile) {
        rootStore.taskStore.setCurrentAssignBoxTaskId(taskId);
      }
    },
    [mobile]
  );

  //render task list
  const renderTaskList = useCallback(
    (tasks: TaskModel[], isMilestonePage: boolean) =>
      tasks.map((task) => (
        <MilestoneTaskRowWrapper
          key={`${item?.id}-${task?.id}`}
          task={task}
          currentProject={currentProject!}
          loadData={loadData}
          item={item}
          onChangeStatus={onChangeStatus}
          onClickTaskCheckBox={onClickTaskCheckBox}
          onOpenDeleteTask={onOpenDeleteTask}
          onOpenTaskModal={onOpenTaskModal}
          maxVisibleLabels={maxVisibleLabels}
          currentUser={currentUser!}
          onClickUsers={onClickUsers}
          isMilestonePage={isMilestonePage}
        />
      )),
    [
      item,
      currentProject,
      loadData,
      onChangeStatus,
      onClickTaskCheckBox,
      onOpenDeleteTask,
      onOpenTaskModal,
      maxVisibleLabels,
      currentUser,
      onClickUsers
    ]
  );

  const menuEditItemsMapped = useMemo(
    () =>
      menuEditItems?.map((menuItem) => (
        <DropdownItem
          label={menuItem.label}
          key={menuItem.key}
          onClick={() => handleMenuClick(menuItem.key, item)}
          tone={menuItem.label === 'Archive' ? 'critical' : 'primary'}
        />
      )),
    [handleMenuClick, item]
  );

  const menuRestoreItemsMapped = useMemo(
    () =>
      menuRestoreItems?.map((menuItem) => (
        <DropdownItem
          label={menuItem.label}
          key={menuItem.key}
          onClick={() => handleMenuOptionClick(menuItem.key, item)}
          tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
        />
      )),
    [handleMenuOptionClick, item]
  );

  return (
    <React.Fragment key={`milestone-${item?.id}`}>
      <MilestoneItemContent>
        <MilestoneHeader
          item={item}
          isMilestoneItem={isMilestoneItem}
          menuEditItemsMapped={menuEditItemsMapped}
          menuRestoreItemsMapped={menuRestoreItemsMapped}
          isSortableItem={isSortableItem}
          sortableAttributes={sortableAttributes}
          sortableListeners={sortableListeners}
        />
        {item?.tasks?.length > 0 && (
          <Second hasContent={item?.tasks?.length > 0}>
            <Content>
              <p>
                {dayjs(newOldestDate).format('DD MMMM')} {!isEmpty(newestDueDate) && '->'}{' '}
                {!isEmpty(newestDueDate) && dayjs(newestDueDate).format('DD MMMM')}
              </p>
              <SVGDiv>
                <SVGIcon name='gray-icon' width='4' height='4' viewBox='0 0 4 4' fill='var(--text-secondary)' />
              </SVGDiv>
              <p>
                {completedTaskList?.length} Tasks completed out of {item?.tasks?.length}
              </p>
            </Content>
            {isMilestoneItem ? (
              <Button
                title='Create Task'
                variant={BUTTON_TYPE.SECONDARY}
                size='small'
                onClick={() => openCreateTaskModel(item)}
              />
            ) : (
              <></>
            )}
          </Second>
        )}
        {item?.tasks?.length === 0 && (
          <MilestoneEmpty>
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.SMALL}
              imageUrl={'mytask_empty'}
              title='No task created yet'
              onClick={() => openCreateTaskModel(item)}
              buttonTitle={isMilestoneItem ? 'Create Task' : ''}
              className='milestone-empty'
            />
          </MilestoneEmpty>
        )}
        {item?.tasks?.length > 0 && (
          <>
            {uncompletedTaskList?.length > 0 && (
              <Data isShowAllCompletedTask={completedTaskList.length === item.tasks.length}>
                {renderTaskList(uncompletedTaskList, true)}
              </Data>
            )}
            {completedTaskList?.length > 0 && (
              <>
                <ShowTask>
                  {selectedId === item?.id && showCompletedTask ? (
                    <ShowHide role='button' onClick={() => toggleCompletedTask(false, '')}>
                      Hide Completed Tasks
                    </ShowHide>
                  ) : (
                    <ShowHide role='button' onClick={() => toggleCompletedTask(true, item?.id)}>
                      Show Completed Tasks
                    </ShowHide>
                  )}
                  {selectedId === item?.id && showCompletedTask && (
                    <Data isShowCompletedTask={true}>{renderTaskList(completedTaskList, false)}</Data>
                  )}
                </ShowTask>
              </>
            )}
          </>
        )}
      </MilestoneItemContent>
    </React.Fragment>
  );
};

export default React.memo(observer(MilestoneItem));
